import {
  CONSOLE_PATHS,
  PLAN_FEATURE_MIN_PLAN,
  SERVICE_NAV_REQUIREMENTS,
  type IconName,
  type PermissionKey,
  type PlanKey,
  type TenantNavId,
} from "@sagot/constants";

/** The ten Service pages (scope 16.1); each opens a shell until its phase ships. */
export const SERVICE_AREA_IDS = [
  "tickets",
  "organisations",
  "contacts",
  "assets",
  "installations",
  "visits",
  "orders",
  "catalogue",
  "service-reports",
  "service-settings",
] as const satisfies readonly TenantNavId[];

export type ServiceAreaId = (typeof SERVICE_AREA_IDS)[number];

export interface ServiceArea {
  readonly id: ServiceAreaId;
  /** Route pattern the shell is mounted on. */
  readonly path: string;
  readonly icon: IconName;
  readonly permission: PermissionKey;
  /** The lowest plan that includes the page (the add-on route to it is not shown here). */
  readonly minPlan: PlanKey;
}

const PATHS: Readonly<Record<ServiceAreaId, string>> = {
  tickets: CONSOLE_PATHS.tickets,
  organisations: CONSOLE_PATHS.organisations,
  contacts: CONSOLE_PATHS.contacts,
  assets: CONSOLE_PATHS.assets,
  installations: CONSOLE_PATHS.installations,
  visits: CONSOLE_PATHS.visits,
  orders: CONSOLE_PATHS.orders,
  catalogue: CONSOLE_PATHS.catalogue,
  "service-reports": CONSOLE_PATHS.serviceReports,
  "service-settings": CONSOLE_PATHS.serviceSettings,
};

const ICONS: Readonly<Record<ServiceAreaId, IconName>> = {
  tickets: "ticket",
  organisations: "building-2",
  contacts: "contact",
  assets: "cpu",
  installations: "hard-hat",
  visits: "truck",
  orders: "shopping-cart",
  catalogue: "package",
  "service-reports": "chart-column",
  "service-settings": "settings-2",
};

export const SERVICE_AREAS: readonly ServiceArea[] = SERVICE_AREA_IDS.map((id) => {
  const requirement = SERVICE_NAV_REQUIREMENTS[id];
  return {
    id,
    path: PATHS[id],
    icon: ICONS[id],
    permission: requirement.permission,
    minPlan: PLAN_FEATURE_MIN_PLAN[requirement.feature],
  };
});

export function serviceArea(id: ServiceAreaId): ServiceArea {
  const found = SERVICE_AREAS.find((area) => area.id === id);
  if (!found) throw new Error(`unknown service area ${id}`);
  return found;
}
